import { useCallback, useState } from 'react';

/**
 * Which of a page's state groups a viewer has collapsed, remembered per
 * `scope` (issue #68): a repo's project page has two lists that collapse
 * independently (its tasks, its jobs), and the choice persists across repos
 * and reloads - collapsing "Closed" on one project's tasks collapses it on
 * the next one too, the same way density or theme is one standing choice
 * rather than something the page resets on every visit.
 *
 * Same shape as `DensityProvider`: read once from `localStorage` in the
 * initializer, write through on every change, and fall back quietly when
 * storage is denied - a browser that refuses it still gets the toggle, just
 * not the memory.
 */
function storageKey(scope: string): string {
  return `dyspatch.collapsed.${scope}`;
}

/** Null means "nothing stored yet": the caller's own defaults apply, not "collapse everything". */
function readCollapsed(scope: string): Set<string> | null {
  try {
    const raw = localStorage.getItem(storageKey(scope));
    return raw ? new Set(JSON.parse(raw) as string[]) : null;
  } catch {
    return null;
  }
}

function writeCollapsed(scope: string, keys: ReadonlySet<string>): void {
  try {
    localStorage.setItem(storageKey(scope), JSON.stringify([...keys]));
  } catch {
    // as above
  }
}

export interface CollapsedGroups {
  isCollapsed: (key: string) => boolean;
  toggle: (key: string) => void;
}

/**
 * `keys` is every group this scope can show, `defaultOpen` the ones a
 * first-time viewer sees open - the live states. A group added to `keys`
 * later that nobody has an opinion on yet starts open, the same rule as a
 * brand-new one: collapsing is something a viewer does, not the default a
 * new state is born into.
 */
export function useCollapsedGroups(
  scope: string,
  keys: readonly string[],
  defaultOpen: readonly string[],
): CollapsedGroups {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(
    () => readCollapsed(scope) ?? new Set(keys.filter((key) => !defaultOpen.includes(key))),
  );

  const toggle = useCallback(
    (key: string) => {
      setCollapsed((previous) => {
        const next = new Set(previous);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        writeCollapsed(scope, next);
        return next;
      });
    },
    [scope],
  );

  return { isCollapsed: (key) => collapsed.has(key), toggle };
}
